import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth-compat';

function useIsReady() {
  const auth = useAuth();
  const hasLocalIdentity = typeof document !== 'undefined' && document.cookie.includes('X-Local-Identity=');
  return auth.isAuthenticated || hasLocalIdentity;
}

import {
  getPublicTeacherReviews,
  getMyReviewFor,
  upsertReview,
  getMyTeacherReviews,
  upsertReply,
  flagReview,
  type UpsertReviewRequest,
} from '@/services/reviewService';

// --- Public reviews (not auth-gated) ---

export function usePublicTeacherReviews(profileIdOrHandle: string, page = 0, size = 10) {
  return useQuery({
    queryKey: ['publicTeacherReviews', profileIdOrHandle, page, size],
    queryFn: () => getPublicTeacherReviews(profileIdOrHandle, page, size),
    enabled: !!profileIdOrHandle,
  });
}

// --- Student review hooks ---

export function useMyReviewFor(teacherUserId: string) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const isReady = useIsReady();

  return useQuery({
    queryKey: ['myReviewFor', teacherUserId],
    queryFn: () => getMyReviewFor(token, teacherUserId),
    enabled: isReady && !!teacherUserId,
  });
}

export function useUpsertReview(teacherUserId: string) {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpsertReviewRequest) => {
      const token = await auth.getToken();
      return upsertReview(token, teacherUserId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'publicTeacherReviews' });
      queryClient.invalidateQueries({ queryKey: ['myReviewFor', teacherUserId] });
    },
  });
}

// --- Teacher review hooks ---

export function useMyTeacherReviews(page = 0, size = 20) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const isReady = useIsReady();

  return useQuery({
    queryKey: ['myTeacherReviews', page, size],
    queryFn: () => getMyTeacherReviews(token, page, size),
    enabled: isReady,
  });
}

export function useUpsertReply() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reviewId, reply }: { reviewId: string; reply: string }) => {
      const token = await auth.getToken();
      return upsertReply(token, reviewId, reply);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myTeacherReviews'] });
    },
  });
}

export function useFlagReview() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reviewId, reason }: { reviewId: string; reason?: string }) => {
      const token = await auth.getToken();
      return flagReview(token, reviewId, reason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myTeacherReviews'] });
    },
  });
}
